import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-[0_2px_4px_-1px_hsl(160_15%_20%/0.1),0_1px_2px_-1px_hsl(160_15%_20%/0.06)] hover:bg-primary/90 hover:shadow-[0_4px_8px_-2px_hsl(160_15%_20%/0.12),0_2px_4px_-2px_hsl(160_15%_20%/0.08)] active:scale-[0.98] active:shadow-[0_1px_2px_0_hsl(160_15%_20%/0.08)]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-[0_2px_4px_-1px_hsl(0_65%_55%/0.15)] hover:bg-destructive/90 hover:shadow-[0_4px_8px_-2px_hsl(0_65%_55%/0.2)] active:scale-[0.98]",
        outline:
          "border border-border/70 bg-background shadow-[0_1px_2px_0_hsl(160_15%_20%/0.04)] hover:bg-accent/8 hover:border-accent/50 hover:text-primary",
        secondary:
          "bg-secondary text-secondary-foreground shadow-[0_1px_3px_-1px_hsl(160_15%_20%/0.06)] hover:bg-secondary/70 hover:shadow-[0_2px_4px_-1px_hsl(160_15%_20%/0.08)]",
        ghost:
          "hover:bg-accent/10 hover:text-primary",
        link:
          "text-primary underline-offset-4 hover:underline",
        accent:
          "bg-accent text-accent-foreground shadow-[0_2px_4px_-1px_hsl(160_50%_45%/0.15)] hover:shadow-[0_0_20px_hsl(160_50%_45%/0.25)] hover:bg-accent/90 active:scale-[0.98]",
        hero:
          "bg-primary text-primary-foreground shadow-[0_4px_12px_-3px_hsl(160_55%_38%/0.3)] hover:shadow-[0_8px_20px_-4px_hsl(160_55%_38%/0.35)] hover:scale-[1.01] active:scale-[0.99] font-semibold",
        "hero-outline":
          "border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground shadow-[0_2px_4px_-1px_hsl(160_15%_20%/0.06)] font-semibold",
        subtle:
          "bg-muted/40 text-muted-foreground hover:bg-muted/70 hover:text-foreground",
        soft:
          "bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-12 rounded-xl px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8 rounded-md",
        "icon-lg": "h-12 w-12 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
